import React from "react";
import { format } from "date-fns";

// Sample UI - uses placeholder data when no real assignments. Wire to assignments + user roles when API returns them.
const ROLE_CONFIG: { key: string; label: string; icon: string }[] = [
  { key: "doctor", label: "Doctors", icon: "🩺" },
  { key: "nurse", label: "Nurses", icon: "💉" },
  { key: "manager", label: "Managers", icon: "📋" },
  { key: "lab", label: "Lab", icon: "🔬" },
  { key: "pharmacy", label: "Pharmacy", icon: "💊" },
];

// Map API role names to our display keys
const ROLE_KEY_MAP: Record<string, string> = {
  doctor: "doctor",
  physicians: "doctor",
  nurse: "nurse",
  nurses: "nurse",
  manager: "manager",
  managers: "manager",
  admin: "manager",
  lab: "lab",
  laboratory: "lab",
  lab_technician: "lab",
  pharmacy: "pharmacy",
  pharmacist: "pharmacy",
};

// Sample data for demo when no real assignments for today
const SAMPLE_PRESENT_BY_ROLE: Record<string, string[]> = {
  doctor: ["Dr. Rajesh Kumar", "Dr. Priya Sharma", "Dr. Amit Patel"],
  nurse: ["Sister Anjali", "Sister Meera", "Brother Suresh"],
  manager: ["Ravi Nair", "Kavita Desai"],
  lab: ["Lab Tech Vikram", "Lab Tech Sunita"],
  pharmacy: ["Pharmacist Deepak", "Pharmacist Neha"],
};

interface WhosOnDutyTodayProps {
  currentShift?: { name: string; startTime: string; endTime: string } | null;
  presentByRole?: Record<string, { id: number; name: string }[]>;
}

const WhosOnDutyToday: React.FC<WhosOnDutyTodayProps> = ({
  currentShift,
  presentByRole,
}) => {
  const today = format(new Date(), "EEEE, MMMM d, yyyy");

  // Use provided shift or sample
  const shift = currentShift || {
    name: "Morning Shift",
    startTime: "09:00",
    endTime: "17:00",
  };

  const getPresentForRole = (roleKey: string) => {
    if (presentByRole?.[roleKey]?.length) {
      return presentByRole[roleKey].map((u) => u.name);
    }
    // Check mapped keys (e.g. presentByRole has "lab_technician" -> map to "lab")
    for (const [apiKey, ourKey] of Object.entries(ROLE_KEY_MAP)) {
      if (ourKey === roleKey && presentByRole?.[apiKey]?.length) {
        return presentByRole[apiKey].map((u) => u.name);
      }
    }
    return SAMPLE_PRESENT_BY_ROLE[roleKey] || [];
  };

  return (
    <div className="mb-10 rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 sm:px-6">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-base font-semibold text-gray-900">
            Who&apos;s On Duty Today
          </h2>
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
            <span>{today}</span>
            <span className="hidden sm:inline">•</span>
            <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-sm font-medium text-primary">
              {shift.name} ({shift.startTime} – {shift.endTime})
            </span>
          </div>
        </div>
      </div>
      <div className="p-4 sm:p-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {ROLE_CONFIG.map(({ key, label, icon }) => {
            const names = getPresentForRole(key);
            return (
              <div
                key={key}
                className="rounded-lg border border-gray-200 bg-gray-50/50 p-4"
              >
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-lg">{icon}</span>
                  <h3 className="text-sm font-semibold text-gray-900">{label}</h3>
                </div>
                <ul className="space-y-1.5">
                  {names.length > 0 ? (
                    names.map((name, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        {name}
                      </li>
                    ))
                  ) : (
                    <li className="text-sm italic text-gray-400">No one on duty</li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WhosOnDutyToday;
